import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, FileText, CheckCircle, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

/**
 * HowItWorks - Reusable 3-step process block
 * Used on: Homepage, Service pages, Town pages
 */
const HowItWorks = ({ variant = 'full' }) => {
  const steps = [
    {
      number: 1,
      icon: Camera,
      title: 'Vous m\'envoyez 2–3 photos + la commune',
      description: 'WhatsApp ou formulaire, c\'est rapide !',
      color: 'emerald'
    },
    {
      number: 2,
      icon: FileText,
      title: 'Je vous fais un devis express',
      description: 'Prix TTC + ≈ après crédit d\'impôt 50%',
      color: 'blue'
    },
    {
      number: 3,
      icon: CheckCircle,
      title: 'Intervention propre et soignée',
      description: 'Vous validez, je facture — c\'est tout !',
      color: 'purple'
    }
  ];

  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-600',
      border: 'border-emerald-200',
      number: 'bg-emerald-600'
    },
    blue: {
      bg: 'bg-blue-100',
      text: 'text-blue-600',
      border: 'border-blue-200',
      number: 'bg-blue-600'
    },
    purple: {
      bg: 'bg-purple-100',
      text: 'text-purple-600',
      border: 'border-purple-200',
      number: 'bg-purple-600'
    }
  };

  // Compact variant for service/town pages
  if (variant === 'compact') {
    return (
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
          Comment ça se passe ?
        </h3>
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {steps.map((step) => (
            <div key={step.number} className="flex-1 flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-200">
              <div className={`w-8 h-8 ${colorClasses[step.color].number} text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0`}>
                {step.number}
              </div>
              <p className="text-sm text-gray-700 font-medium">{step.title}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/devis">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto">
              Devis express
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </Link>
          <a href="https://wa.me/33782586685" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 w-full sm:w-auto">
              <MessageCircle className="mr-2" size={16} />
              WhatsApp
            </Button>
          </a>
        </div>
      </div>
    );
  }

  // Full variant for homepage
  return (
    <section className="py-12 bg-gradient-to-br from-gray-50 to-emerald-50">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3 text-center">
          Comment ça se passe ?
        </h2>
        <p className="text-gray-600 text-center mb-10 max-w-xl mx-auto">
          Un processus simple et transparent en 3 étapes
        </p>

        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const colors = colorClasses[step.color];
            
            return (
              <div key={step.number} className="relative">
                <div className={`bg-white rounded-2xl p-6 border-2 ${colors.border} h-full text-center shadow-sm hover:shadow-md transition-shadow`}>
                  {/* Step number */}
                  <div className={`w-10 h-10 ${colors.number} text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-4`}>
                    {step.number}
                  </div>
                  
                  {/* Icon */}
                  <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                    <Icon className={`w-7 h-7 ${colors.text}`} />
                  </div>
                  
                  {/* Content */}
                  <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
                
                {/* Arrow between cards (hidden on mobile and last card) */}
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 text-gray-300 z-10">
                    <ArrowRight size={24} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/devis">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8">
              Devis express
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
          <a href="https://wa.me/33782586685" target="_blank" rel="noopener noreferrer">
            <Button size="lg" variant="outline" className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-bold px-8">
              <MessageCircle className="mr-2" size={18} />
              WhatsApp
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
